/**
 * CRM page (PRIVATE fork) — customer relationship management module.
 *
 * Contributed as a top-level page via page-registry's EXTRA_PAGES seam. The shell
 * is self-contained: it renders its own sub-module tab bar (fork-owned `crmModules`)
 * and caches sub-panes across switches, rather than depending on the upstream
 * settings-scoped nav-registry / global sidebar panel.
 *
 * Sub-modules: home · companies · contacts · products · deals · followups · influencers.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ErrorBoundary } from '../../components/ui';
import { CachedModule } from '../../components/app/module-page-shell';
import { usePageActions } from '../../hooks/usePageActions';
import { crmModules } from './nav';

// Sub-pages
import { CrmHome } from './home';
import { CompaniesPage } from './companies';
import { CompanyDetailPage } from './company-detail';
import { ContactsPage } from './contacts';
import { ContactDetailPage } from './contact-detail';
import { ProductsPage } from './products';
import { FollowupsPage } from './followups';
import { DealsPage } from './deals';
import { DealDetailPage } from './deal-detail';
import { InfluencersPage } from './influencers';

// ─── Route parsing ───────────────────────────────────────

function parseCrmRoute(subPath: string): { module: string; detailId: number | null } {
  const segments = subPath.split('/').filter(Boolean);
  const module = segments[0] || 'home';
  const detailId = segments[1] ? parseInt(segments[1], 10) : null;
  return { module, detailId: detailId && !isNaN(detailId) ? detailId : null };
}

const CRM_NAV_MODULES = crmModules.map((m) => m.key);

// ─── Sub-module tab bar (desktop + mobile) ───────────────

function tabClasses(active: boolean) {
  return [
    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-colors border',
    active
      ? 'bg-primary-subtle text-primary-fg-strong font-medium border-primary-edge'
      : 'text-fg-muted hover:text-fg-secondary border-transparent',
  ].join(' ');
}

function CrmTabBar({ activeKey }: { activeKey: string }) {
  return (
    <div className="md:hidden flex items-center gap-1.5 px-3 py-2 border-b border-edge bg-surface-raised overflow-x-auto scrollbar-hide flex-shrink-0">
      {crmModules.map((mod) => {
        const Icon = mod.icon;
        const active = activeKey === mod.key;
        return (
          <button
            key={mod.key}
            type="button"
            onClick={() => {
              window.location.hash = mod.path;
            }}
            className={tabClasses(active)}
          >
            <Icon size={13} className={active ? 'text-primary-fg' : 'text-fg-faint'} />
            {mod.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────

export function CrmPage({ subPath }: { subPath: string }) {
  const { module: activeModule, detailId } = useMemo(() => parseCrmRoute(subPath), [subPath]);

  // ── Agent UI actions: let the Global Agent operate the CRM screen ──
  // The agent reads the current view via crm_get_current_view and navigates the user
  // via crm_navigate. Both are 'auto' (pure UI, no data writes — those stay on the
  // confirmed server-side crm_manager tool).
  usePageActions(
    [
      {
        name: 'crm_navigate',
        description:
          'Open a CRM sub-page or a specific record for the user. `module` is one of: ' +
          CRM_NAV_MODULES.join(', ') +
          '. To open a specific record, pass its numeric `id` (companies, contacts and deals have detail pages).',
        safety: 'auto',
        parameters: {
          type: 'object',
          properties: {
            module: { type: 'string', enum: CRM_NAV_MODULES, description: 'CRM sub-module to open' },
            id: { type: 'number', description: 'Optional record id to open a detail page (companies/contacts/deals)' },
          },
          required: ['module'],
          additionalProperties: false,
        },
        execute: (params) => {
          const mod = String(params.module ?? 'home');
          if (!CRM_NAV_MODULES.includes(mod)) return { ok: false, error: `Unknown CRM module: ${mod}` };
          const id = params.id;
          const hash = id != null && id !== '' ? `#/crm/${mod}/${id}` : `#/crm/${mod}`;
          window.location.hash = hash;
          return { ok: true, navigatedTo: hash };
        },
      },
      {
        name: 'crm_get_current_view',
        description:
          'Read which CRM page the user is currently looking at — returns the module, whether it is a list or detail view, and the record id if any.',
        safety: 'auto',
        parameters: { type: 'object', properties: {}, additionalProperties: false },
        execute: () => {
          const segs = (window.location.hash || '').replace(/^#\/?/, '').split('?')[0].split('/').filter(Boolean); // ['crm', module?, id?]
          const module = segs[1] || 'home';
          const itemId = segs[2] || null;
          return { module, view: itemId ? 'detail' : 'list', itemId };
        },
      },
    ],
    [],
  );

  // Track visited modules for state caching
  const [visitedModules, setVisitedModules] = useState<Set<string>>(() => new Set([activeModule]));

  useEffect(() => {
    setVisitedModules((prev) => {
      if (prev.has(activeModule)) return prev;
      return new Set(prev).add(activeModule);
    });
  }, [activeModule]);

  // If we have a detail ID, render the detail page directly (full-bleed, no tab bar).
  if (detailId !== null) {
    if (activeModule === 'companies') return <CompanyDetailPage companyId={detailId} />;
    if (activeModule === 'contacts') return <ContactDetailPage contactId={detailId} />;
    if (activeModule === 'deals') return <DealDetailPage dealId={detailId} />;
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <CrmTabBar activeKey={activeModule} />
      <main className="flex-1 overflow-hidden relative">
        <ErrorBoundary>
          <CachedModule active={activeModule === 'home'} visited={visitedModules.has('home')}>
            <CrmHome />
          </CachedModule>
          <CachedModule active={activeModule === 'companies'} visited={visitedModules.has('companies')}>
            <CompaniesPage />
          </CachedModule>
          <CachedModule active={activeModule === 'contacts'} visited={visitedModules.has('contacts')}>
            <ContactsPage />
          </CachedModule>
          <CachedModule active={activeModule === 'products'} visited={visitedModules.has('products')}>
            <ProductsPage />
          </CachedModule>
          <CachedModule active={activeModule === 'deals'} visited={visitedModules.has('deals')}>
            <DealsPage />
          </CachedModule>
          <CachedModule active={activeModule === 'followups'} visited={visitedModules.has('followups')}>
            <FollowupsPage />
          </CachedModule>
          <CachedModule active={activeModule === 'influencers'} visited={visitedModules.has('influencers')}>
            <InfluencersPage />
          </CachedModule>
        </ErrorBoundary>
      </main>
    </div>
  );
}
